import { customHeaders, requestApi } from 'api/api'
import dayjs from 'dayjs'
import { RestApi } from '@node-space/rest-api'
import {
  PaymentCaseComplete,
  PaymentCaseCustomerId,
  PaymentCaseDocumentsResponse,
  PaymentCaseEmailsResponse,
  PaymentCaseSendEmail,
  PaymentCasesResponse,
  PaymentCaseType,
  PaymentCaseUploadDocument,
  PaymentsCreateRequest,
} from 'types/payments'
import { Paging } from 'types/types'
import { isTestEnvironment } from 'utils/utils'

/**
 * POST Create a Payment
 */
export const postCreatePayment = async (payload: PaymentsCreateRequest) => {
  const createPaymentInEndpoint = '/v1/pay/summary'

  return requestApi({
    url: createPaymentInEndpoint,
    method: 'POST',
    data: payload,
  })
}

/**
 * PUT Mark a still PROCESSING but incomplete Payment with aleast one Payment leg to be COMPLETE
 */
export const putMarkUnderpaidPaymentComplete = (uuid: string) => {
  return RestApi.put(!isTestEnvironment, `v1/pay/${uuid}/underpay`, {}, customHeaders)
}

/**
 * GET All the payments and optional export CSV
 */
export const getAllPayments = ({
  paging,
  from,
  to,
  merchant,
  status,
  searchByRef,
  searchByTransactionHash,
}: {
  paging: Paging
  from: string | Date
  to: string | Date
  merchant: string
  status: string
  searchByRef?: string
  searchByTransactionHash?: string
}): Promise<any> => {
  let searchString = ''

  if (searchByRef) {
    searchString = `&q=${encodeURI(searchByRef)}`
  } else if (searchByTransactionHash) {
    searchString = `&transactionHash=${encodeURI(searchByTransactionHash)}`
  } else {
    searchString = ''
  }
  const statusFilter = status !== 'ALL' ? `&status=${status}` : ''
  const merchantFilter = merchant != 'ALL' ? `&merchantId=${merchant}` : ''

  return RestApi.pagedGet(
    !isTestEnvironment,
    `v1/pay/ui?order=desc&sort=id&fromDate=${from}&toDate=${to}${searchString}${merchantFilter}${statusFilter}`,
    paging,
    customHeaders
  )
}

/**
 * GET All the Channel payments
 */
export const getAllChannelsPayments = (
  paging: Paging,
  from: string,
  to: string,
  merchant: string,
  status: string,
  search: string
): Promise<any> => {
  const fromDateString = dayjs(from).format('YYYY-MM-DD')
  const toDateString = dayjs(to).format('YYYY-MM-DD')
  const searchString = search ? `&q=${encodeURI(search)}` : ''
  const statusFilter = status !== 'ALL' ? `&status=${status}` : ''
  const merchantFilter = merchant != 'ALL' ? `&merchantId=${merchant}` : ''

  return RestApi.pagedGet(
    !isTestEnvironment,
    `v2/channel/payment?order=desc&sort=id&fromDate=${fromDateString}&toDate=${toDateString}${searchString}${merchantFilter}${statusFilter}`,
    paging,
    customHeaders
  )
}

export const getAllPaymentsCSV = (
  from: string,
  to: string,
  status: string,
  search: string,
  merchant: string
) => {
  return RestApi.getStream(
    !isTestEnvironment,
    `v1/pay/summary`,
    '',
    {
      export: true,
      format: 'CSV',
      fromDate: from,
      toDate: to,
      status: (status !== 'ALL' && status) || '',
      merchantId: (merchant !== 'ALL' && merchant) || '',
      ...(search && { q: encodeURI(search) }),
    },
    customHeaders
  )
}

export const getAllChannelsPaymentsCSV = (
  from: string,
  to: string,
  status: string,
  search: string,
  merchant: string
) => {
  return RestApi.getStream(
    !isTestEnvironment,
    `v2/channel/payment`,
    '',
    {
      export: true,
      format: 'CSV',
      fromDate: from,
      toDate: to,
      status: (status !== 'ALL' && status) || '',
      merchantId: (merchant !== 'ALL' && merchant) || '',
      ...(search && { q: encodeURI(search) }),
    },
    customHeaders
  )
}

export const getPaymentSupportCases = (
  paging: Paging,
  status?: PaymentCaseType[]
): Promise<PaymentCasesResponse> => {
  const { offset, max } = paging

  return requestApi({
    url: '/salesforce-facade/v1/case-management/cases',
    method: 'GET',
    params: {
      ...(max && { max }),
      offset,
      order: 'desc',
      ...(status?.length && { status: status?.join(';') }),
    },
  })
}

export const getPaymentCaseDocuments = (
  paymentCaseId: string
): Promise<PaymentCaseDocumentsResponse> => {
  return requestApi({
    url: '/salesforce-facade/v1/case-management/documents',
    method: 'GET',
    params: {
      ...(paymentCaseId && { caseId: paymentCaseId }),
      objectName: 'file',
      order: 'asc',
    },
  })
}

export const getPaymentCaseEmails = (paymentCaseId: string): Promise<PaymentCaseEmailsResponse> => {
  return requestApi({
    url: '/salesforce-facade/v1/case-management/emails',
    method: 'GET',
    params: {
      ...(paymentCaseId && { caseId: paymentCaseId }),
      objectName: 'email',
      order: 'desc',
    },
  })
}

export const getPaymentCaseDocument = (paymentDocumentId: string) => {
  return requestApi({
    url: `/salesforce-facade/v1/case-management/documents/${paymentDocumentId}`,
    method: 'GET',
    config: {
      responseType: 'arraybuffer',
    },
  })
}

export const postPaymentCaseEmail = (email: PaymentCaseSendEmail) => {
  return requestApi({
    url: '/salesforce-facade/v1/case-management/emails',
    method: 'POST',
    data: email,
  })
}

export const postPaymentCaseDocument = (document: PaymentCaseUploadDocument) => {
  return requestApi({
    url: '/salesforce-facade/v1/case-management/documents',
    method: 'POST',
    data: document,
    timeout: 120000, // 2 minutes
  })
}

export const postPaymentCaseCustomerId = (customerId: PaymentCaseCustomerId) => {
  return requestApi({
    url: '/salesforce-facade/v1/case-management/customerId',
    method: 'POST',
    data: customerId,
  })
}

export const postPaymentCaseCompleted = (caseCompleted: PaymentCaseComplete) => {
  return requestApi({
    url: '/salesforce-facade/v1/case-management/complete',
    method: 'POST',
    data: caseCompleted,
  })
}
